import * as React from "react";

import type { OrgLlmModel } from "@/shared/domain/orgs";
import { RobotoButton, Select } from "@/shared/foundation/primitives";

import styles from "./OrgAiModels.module.css";
import {
  robotoChoiceLabel,
  type RobotoModelChoice,
} from "./robotoModelChoices";

interface AddRobotoModelProps {
  /** The Roboto-managed models not on the list yet. */
  choices: readonly RobotoModelChoice[];
  onAdd: (model: OrgLlmModel) => void;
  onCancel: () => void;
  /** The add is being saved. */
  pending?: boolean;
}

/** Pick a Roboto-managed model (billed through AI credits) and add it to the list, or cancel. */
export function AddRobotoModel({
  choices,
  onAdd,
  onCancel,
  pending = false,
}: AddRobotoModelProps) {
  const [pickedId, setPickedId] = React.useState<string | null>(null);
  const picked = choices.find((c) => c.model.id === pickedId);

  if (choices.length === 0) {
    return null;
  }
  return (
    <div className={styles.addRow} role="group" aria-label="Add a Roboto model">
      <Select<string>
        label="Add Roboto model"
        placeholder="Pick a model"
        items={choices.map((c) => ({
          value: c.model.id,
          label: robotoChoiceLabel(c),
          disabled: c.unavailableReason !== undefined,
        }))}
        value={picked === undefined ? null : pickedId}
        onValueChange={setPickedId}
        disabled={pending}
        className={styles.addSelect}
      />
      <RobotoButton
        size="sm"
        disabled={picked === undefined}
        loading={pending}
        onClick={() => {
          if (picked !== undefined) {
            onAdd(picked.model);
          }
        }}
      >
        Add
      </RobotoButton>
      <RobotoButton
        variant="ghost"
        size="sm"
        onClick={onCancel}
        disabled={pending}
      >
        Cancel
      </RobotoButton>
    </div>
  );
}
